import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  MyLeadsCreateAudienceRequestParams,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import { notification } from 'antd';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { useExportContactsAction } from '../../Audiences/ExportContacts/ExportContacts';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { getEnumeratedFilters } from './use-filter-my-leads';
import { getESStructure } from '../../Audiences/Audiences';

export const useCreateMyLeadsAudience = (onSuccess?: () => void) => {
  const { create, update } = useAudiencesCRUDActions();
  const { openAddAudienceToAutomationModal, closeModal } = useModalActions();

  const onExportSuccess = (name?: string) => {
    notification.success({
      message: name
        ? `Audience ${name} export has been scheduled.`
        : 'Contacts export has been scheduled.',
    });
    onSuccess?.();
  };
  const exportContacts = useExportContactsAction();
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const onAddToAutomation = (activeAudience: AudiencesModel) => {
    openAddAudienceToAutomationModal({
      activeAudience,
      closeModal,
      usePurchaseLeadsLayout: true,
    });
  };

  const createMyLeadsAudience = async ({
    params,
    withAddToAutomation,
    withExport,
    isSelectedAll,
  }: {
    params: MyLeadsCreateAudienceRequestParams;
    withAddToAutomation?: boolean;
    withExport?: boolean;
    isSelectedAll?: boolean;
  }) => {
    const { tagsFilters, filterParams, esFilters, ...restParams } =
      params || {};

    const hasFilters = () => ({
      filterParamsExist: !!Object.keys(filterParams || {}).length,
      tagsFiltersExist: !!Object.keys(tagsFilters || {}).length,
      esFiltersExist: !!Object.keys(esFilters || {}).length,
    });
    const { filterParamsExist, tagsFiltersExist, esFiltersExist } =
      hasFilters();

    const contactsCount =
      (params?.limit || 0) - (params.excludedLeadIds?.length || 0) ||
      params.leadIds?.length ||
      0;

    if (
      isSelectedAll &&
      withExport &&
      !(filterParamsExist || tagsFiltersExist || esFiltersExist)
    ) {
      exportContacts(undefined, contactsCount, () => onExportSuccess());
      return;
    }

    const enumeratedFilterParams = getEnumeratedFilters(filterParams, true);
    const enumeratedTags = getEnumeratedFilters(tagsFilters, false);

    const audience = await create(
      {
        ...restParams,
        filterParams: enumeratedFilterParams,
        tagsFilters: enumeratedTags,
        esFilters,
      },
      chatbotId,
    );

    if (audience) {
      notification.success({
        message: `Audience ${audience.name} has been created.`,
      });

      if (withAddToAutomation) {
        onAddToAutomation(audience);
        return;
      }

      if (withExport) {
        exportContacts(audience.id, contactsCount, () =>
          onExportSuccess(audience.name),
        );
        return;
      }

      history.push(
        BotBuilderRoutes.audiences.getLink({
          chatbotId,
          audienceId: audience.id,
        }),
      );
    }
  };

  const updateMyLeadsAudience = async (
    filterParams?: MyLeadsSearchQuery,
    activeAudience?: AudiencesModel,
  ) => {
    if (activeAudience) {
      const enumeratedFilterParams = getEnumeratedFilters(
        filterParams?.filterParams,
        true,
      );
      const enumeratedTags = getEnumeratedFilters(
        filterParams?.tagsFilters,
        false,
      );

      await update(
        {
          ...activeAudience,
          filterParams: enumeratedFilterParams,
          tagsFilters: enumeratedTags,
          esFilters: getESStructure(filterParams?.query),
        },
        chatbotId,
      );
    }
  };

  return { createMyLeadsAudience, updateMyLeadsAudience };
};
